/**
 * StepTimeline.tsx — vertical step list with accent line.
 *
 * Upgraded visual treatment over the numbered H3 pattern.
 * Use in multi-step posts (5+ steps) where numbered-H3 lacks visual presence.
 * NOT for ≤3 steps — use a standard numbered list or H3 heading pattern.
 * NOT retrofitted to existing migrated posts (cycles 1–4) — additive only.
 *
 * CSS: .blog-step-timeline and .blog-step-timeline__* in globals.css
 *   Disc: 36px circle, --cream-3 background, --gold-on-cream number.
 *   Vertical line: CSS ::before pseudo-element, centered on disc column.
 *   Item gap: --space-48 (48px) — generous breathing room.
 *   Step heading renders as <h3> for correct heading hierarchy.
 *   Disc number is aria-hidden; <h3> carries the semantic identity.
 *
 * Two disc modes per item:
 *   Auto-numbered (no icon prop): renders the step number as text.
 *   Icon disc (icon prop set): renders a philosophy icon at 20×20 inside disc.
 *
 * Parent component: <stepTimeline>
 * Child component:  <stepTimelineItem>
 *
 * Usage in MDX:
 *   <stepTimeline>
 *     <stepTimelineItem heading="Start with the wrist free">
 *       Before any note, your wrist must be able to rotate freely.
 *     </stepTimelineItem>
 *     <stepTimelineItem heading="Feel the arm weight" icon="wrist-freedom">
 *       Let gravity do the work.
 *     </stepTimelineItem>
 *   </stepTimeline>
 *
 * Added 2026-05-09 per Aria component spec (video-blog migrations cycle 5).
 */

import React from 'react';
import PhilosophyIcon, { type PhilosophyIconName } from './PhilosophyIcon';

// ---------------------------------------------------------------------------
// StepTimelineItem
// ---------------------------------------------------------------------------

interface StepTimelineItemProps {
  heading: string;
  icon?: PhilosophyIconName;
  /**
   * Auto-counter injected by StepTimeline parent. Not a public prop — do not
   * pass from MDX; the parent sets it via React.cloneElement.
   */
  _index?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}

export function StepTimelineItem({ heading, icon, _index = 1, children }: StepTimelineItemProps) {
  return (
    <li className="blog-step-timeline__item">
      <div
        className={`blog-step-timeline__disc${icon ? ' blog-step-timeline__disc--icon' : ''}`}
        aria-hidden="true"
      >
        {icon
          ? <PhilosophyIcon name={icon} width={20} height={20} />
          : _index
        }
      </div>
      <div className="blog-step-timeline__content">
        <h3 className="blog-step-timeline__heading">{heading}</h3>
        <div className="blog-step-timeline__body">
          {children}
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// StepTimeline (parent)
// ---------------------------------------------------------------------------

interface StepTimelineProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}

export default function StepTimeline({ children }: StepTimelineProps) {
  // Detect whether all children have an icon prop — if so, emit <ul>; else <ol>.
  // Mixed icon + number in a single timeline is not supported per Aria spec.
  const childArray = React.Children.toArray(children);

  // Inject _index (1-based) into each StepTimelineItem child so the disc
  // can display the correct auto-number without relying on CSS counters
  // (CSS counters would require list-style:decimal on <ol>, which conflicts
  // with the custom disc styling).
  const indexedChildren = childArray.map((child, i) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<StepTimelineItemProps>, {
        _index: i + 1,
      });
    }
    return child;
  });

  // Determine list type: if any child lacks an icon, default to <ol>.
  const allHaveIcon = childArray.every(
    (child) =>
      React.isValidElement(child) &&
      (child as React.ReactElement<StepTimelineItemProps>).props.icon != null,
  );

  const ListEl = allHaveIcon ? 'ul' : 'ol';

  return (
    <ListEl className="blog-step-timeline">
      {indexedChildren}
    </ListEl>
  );
}
